import { Action, ActionReducer, INIT, UPDATE } from '@ngrx/store';

import { isPresent } from '@fcom/core/utils/utils';

import { Storage } from '../interfaces';
import { isISODateString } from './date';
import { mergeDeep } from './merge';
import { LocalDate } from './tz-date';

export interface StorageKey {
  name: string;
  keys?: string[];
}

export interface StorageConfig {
  storage: Storage;
  rehydrate: boolean;
  keys: StorageKey[];
}

export interface ExtendedAction extends Action {
  payload?: any;
  features?: string[];
}

export interface StateData {
  [key: string]: any;
}

const localDateReviver = (_k, v) => (isISODateString(v) ? new LocalDate(v) : v);

const pickKeys = (obj: string | StateData, keys: string[] = []) => {
  if (typeof obj === 'string') {
    return '';
  }
  return keys.reduce((values: StateData, key: string) => {
    values[key] = obj?.[key];
    return values;
  }, {});
};

const syncStateToStorage = <State>(config: StorageConfig, state: State) => {
  config.keys.forEach((key: StorageKey) => {
    const partOfState: string | StateData = state[key.name];
    if (isPresent(partOfState)) {
      const newPartOfState: string | StateData = Array.isArray(key.keys)
        ? pickKeys(partOfState, key.keys)
        : partOfState;
      config.storage.set(
        key.name,
        typeof newPartOfState === 'string' ? newPartOfState : JSON.stringify(newPartOfState)
      );
    }
  });
};

const parseData = (jsonString: any) => {
  try {
    return JSON.parse(jsonString, localDateReviver);
  } catch (e) {
    return jsonString;
  }
};

const getStateFromStorage = (config: StorageConfig) => {
  if (config.rehydrate) {
    return config.keys.reduce((stateFromStorage, key: StorageKey) => {
      const storageData = config.storage.get(key.name);
      if (isPresent(storageData)) {
        return mergeDeep(stateFromStorage, { [key.name]: parseData(storageData) });
      }
      return stateFromStorage;
    }, {});
  }
  return {};
};

export const storageSync =
  (config: StorageConfig) =>
  <State>(reducer: ActionReducer<State, ExtendedAction>) => {
    return function (state: State, action: ExtendedAction) {
      const stateToUpdate: State =
        action.type === UPDATE || action.type === INIT ? mergeDeep(state, getStateFromStorage(config)) : state;
      const nextState: State = reducer(stateToUpdate, action);
      syncStateToStorage(config, nextState);
      return nextState;
    };
  };
